import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Download, ArrowDown } from "lucide-react";
import { profile, hero } from "../../data/portfolioData";
import { scrollToSection } from "./shared";

const NodeCanvas = () => {
  const canvasRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: 0.5, y: 0.5 };
    let nodes = [];

    const build = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.floor((w * h) / 16000);
      nodes = Array.from({ length: Math.max(count, 24) }, (_, i) => ({
        bx: Math.random() * w,
        by: Math.random() * h,
        r: Math.random() * 1.6 + 0.8,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        amber: i % 6 === 0,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const px = (mouse.x - 0.5) * 26;
      const py = (mouse.y - 0.5) * 26;
      for (const n of nodes) {
        if (!reduced) {
          n.bx += n.vx;
          n.by += n.vy;
          if (n.bx < 0 || n.bx > w) n.vx *= -1;
          if (n.by < 0 || n.by > h) n.vy *= -1;
        }
        n.x = n.bx + px * n.r * 0.4;
        n.y = n.by + py * n.r * 0.4;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 130) {
            ctx.strokeStyle = `rgba(148,163,184,${0.14 * (1 - d / 130)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.amber ? "rgba(245,158,11,0.9)" : "rgba(148,163,184,0.5)";
        if (n.amber) {
          ctx.shadowColor = "rgba(245,158,11,0.8)";
          ctx.shadowBlur = 10;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = (e.clientX - rect.left) / rect.width;
      mouse.y = (e.clientY - rect.top) / rect.height;
    };

    build();
    draw();
    window.addEventListener("resize", build);
    window.addEventListener("mousemove", onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", build);
      window.removeEventListener("mousemove", onMove);
    };
  }, [reduced]);

  return <canvas ref={canvasRef} className="absolute inset-0" aria-hidden="true" />;
};

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden grid-bg noise-overlay" data-testid="hero-section">
      <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-amber-500/[0.07] blur-[120px]" />
      <div className="absolute top-1/3 -right-40 h-[420px] w-[420px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-20 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center relative">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex flex-wrap items-center gap-2.5 mb-8"
            data-testid="availability-badge"
          >
            <span className="inline-flex items-center gap-2.5 rounded-full border border-emerald-400/25 bg-emerald-400/[0.07] px-4 py-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
              <span className="text-xs sm:text-[13px] text-emerald-300/90 font-medium">{profile.availability}</span>
            </span>
            <span className="inline-flex items-center gap-2.5 rounded-full border border-amber-400/25 bg-amber-400/[0.07] px-4 py-1.5">
              <span className="h-2 w-2 rounded-full bg-amber-400" />
              <span className="text-xs sm:text-[13px] text-amber-300/90 font-medium">{hero.openToRoles}</span>
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-6xl lg:text-7xl text-slate-50" data-testid="hero-headline">
            {hero.headline.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className={`block ${i === hero.headline.length - 1 ? "text-gradient-amber" : ""}`}
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-6 max-w-xl text-sm sm:text-base lg:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-subtext"
          >
            {hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("work")}
              className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_36px_-8px_rgba(245,158,11,0.7)]"
              data-testid="hero-cta-work"
            >
              View My Work
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-amber-400/50 hover:text-amber-300"
              data-testid="hero-cta-contact"
            >
              Let's Work Together
            </button>
            <a
              href={profile.resumeUrl}
              download
              className="inline-flex items-center gap-2 text-sm font-code text-slate-500 hover:text-amber-400 transition-colors"
              data-testid="hero-resume-link"
            >
              <Download size={15} /> Download Resume
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-14 flex gap-10 sm:gap-14"
            data-testid="hero-metrics"
          >
            {hero.metrics.map((m) => (
              <div key={m.label}>
                <p className="font-display text-2xl sm:text-3xl font-bold text-slate-100">{m.value}</p>
                <p className="mt-1 text-[11px] sm:text-xs font-code uppercase tracking-wider text-slate-500">{m.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative h-[480px] rounded-3xl border border-white/[0.07] bg-[#0B0F19]/80 overflow-hidden" data-testid="hero-visual">
            <NodeCanvas />
            <div className="absolute top-5 left-5 glass-card rounded-xl px-4 py-3 font-code text-[11px] text-slate-400">
              <span className="text-emerald-400">●</span> system.online — 5,284 devices
            </div>
            <div className="absolute bottom-5 right-5 glass-card rounded-xl px-4 py-3 font-code text-[11px] text-slate-400">
              deploy <span className="text-amber-400">→</span> production <span className="text-emerald-400">✓</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-14 top-24 w-72 glass-card rounded-2xl overflow-hidden shadow-2xl"
            data-testid="hero-code-card"
          >
            <div className="flex items-center gap-1.5 border-b border-white/[0.06] px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
              <span className="ml-2 font-code text-[10px] text-slate-500">developer.ts</span>
            </div>
            <pre className="p-4 font-code text-[11px] leading-5 text-slate-300">
              <span className="block"><span className="text-violet-400">const</span> developer = {"{"}</span>
              <span className="block pl-4">name: <span className="text-emerald-300">"Vishal Behera"</span>,</span>
              <span className="block pl-4">craft: [<span className="text-emerald-300">"web"</span>, <span className="text-emerald-300">"mobile"</span>, <span className="text-emerald-300">"backend"</span>],</span>
              <span className="block pl-4">ships: <span className="text-amber-300">"production-ready products"</span>,</span>
              <span className="block">{"}"};</span>
            </pre>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 3 }}
            transition={{ duration: 0.9, delay: 1.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -right-7 bottom-12 w-36 rounded-[1.9rem] border border-white/10 bg-[#0A0D15] p-2 shadow-2xl"
            data-testid="hero-app-card"
          >
            <div className="rounded-[1.5rem] bg-gradient-to-b from-[#121A2B] to-[#0A0D15] px-3 pt-3 pb-4 h-60 flex flex-col">
              <div className="flex items-center justify-between">
                <span className="font-code text-[8px] text-slate-500">9:41</span>
                <span className="h-1.5 w-8 rounded-full bg-white/10" />
              </div>
              <div className="mt-4 h-2.5 w-16 rounded-full bg-amber-400/80" />
              <div className="mt-1.5 h-1.5 w-24 rounded-full bg-white/10" />
              <div className="mt-4 space-y-2.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] p-2">
                    <span className={`h-5 w-5 rounded-md ${i === 0 ? "bg-amber-400/70" : i === 1 ? "bg-emerald-400/60" : "bg-cyan-400/60"}`} />
                    <span className="flex-1 space-y-1">
                      <span className="block h-1.5 w-4/5 rounded-full bg-white/15" />
                      <span className="block h-1 w-3/5 rounded-full bg-white/[0.08]" />
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-auto flex justify-center gap-3 pt-3">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
                <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToSection("intro")}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-slate-600 hover:text-amber-400 transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
};
